import type { Product } from '@bbpos/types';

/**
 * C12: the one place the billing scan decides what a looked-up code does.
 * C16 adds its "Not sold at this store" refusal, checked first (G-A2); C05
 * adds its letter-case rule in the lookup. Keep it small and pure.
 */
export type ScanResolution =
  | { kind: 'add'; product: Product }
  | { kind: 'pick'; parent: Product }
  | { kind: 'refuse'; product: Product };

export const NOT_SOLD_HERE = (name: string) => `${name} is not sold at this store.`;

/**
 * The store switched this row off (or, from the feed, it is an unpublished
 * draft). Only an explicit `false` refuses: a row without the field keeps the
 * old behaviour, and the server refusal at Pay is the last net.
 */
export function isNotSoldHere(product: Pick<Product, 'is_active'>): boolean {
  return product.is_active === false;
}

/**
 * A variant parent is never added: the cashier picks a size or colour (G-A1).
 * The flag alone is not enough offline — making a variant does not touch the
 * parent row, so a cached parent can still say `has_variants: false` while its
 * child is cached. The server refusal (VARIANT_REQUIRED) is the last net.
 */
export function isVariantParent(
  product: Pick<Product, 'has_variants'>,
  cachedChildCount: number,
): boolean {
  return product.has_variants === true || cachedChildCount > 0;
}

export async function resolveScan(
  product: Product,
  deps: { countCachedChildren: (parentId: string) => Promise<number> },
): Promise<ScanResolution> {
  // Before the parent check: a switched-off parent refuses, no picker.
  if (isNotSoldHere(product)) return { kind: 'refuse', product };
  // A variant child is never a parent: skip the cache lookup.
  if (product.parent_product_id) return { kind: 'add', product };
  const children = product.has_variants === true ? 0 : await deps.countCachedChildren(product.id);
  return isVariantParent(product, children)
    ? { kind: 'pick', parent: product }
    : { kind: 'add', product };
}
